import formName from 'constants/formName'
import { actions, actionTypes, selectors } from 'data'
import { notifyError } from 'data/App/sagas'
import { ApiSettingsType } from 'data/Auth/types'
import isNil from 'lodash/isNil'
import reverse from 'lodash/reverse'
import trim from 'lodash/trim'
import uniq from 'lodash/uniq'
import { getFormValues } from 'redux-form'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { activateKey, createKey, deactivateKey, getKeys } from 'services/api/apikey'
import { Response } from 'utils/fetchApi'

import { ActivateApiKeyActionType } from './actions'
import { ApiKeyCredentialsType } from './types'

const create = function* () {
  const formData = yield select(getFormValues(formName.API_KEY))
  formData.metadata.ipAddresses = uniq((formData.metadata.ipAddresses ?? []).map(trim))

  const twoFactorStatus = yield select(selectors.auth.getTwoFactorStatus)
  const challenge = yield select(
    selectors.auth.secondFactorChallenge.getCurrentSecondFactorChallenge
  )
  // If 2FA was WALLET_SMS we need to send the challenge id we requested while
  // rendering the form
  const challengeId =
    twoFactorStatus && twoFactorStatus[0] === 'WALLET_SMS' ? challenge?.challengeId : undefined
  const data: Response<ApiKeyCredentialsType> = yield call(createKey, {
    ...formData,
    challengeId
  })

  if (data.ok) {
    yield put(actions.auth.setNewApiKeyCredentials(data.response))
    yield put(actions.app.showModal('API_KEY_SUCCESS'))
    yield put(actions.apiKey.getKeys())
    return
  }

  if (data.error && data.error.status !== 500) {
    // FIXME: when backend fixes 500 issue
    const { cause, status } = data.error
    const errors = {
      e401: 'KO_TWO_FACTOR',
      e404: 'KO_USER_NOT_FOUND',
      edefault: 'KO_GENERIC'
    }
    yield call(
      notifyError,
      !isNil(cause) ? cause : 'Unknown Error',
      errors[`e${!isNil(status) ? status : 'default'}`]
    )
  }
}

const getKeysSaga = function* () {
  const data: Response<ApiSettingsType> = yield call(getKeys)

  // TODO: Handle error case
  if (data.ok) {
    const activeKeys = data.response.keys.filter(
      (key) => key.status === 'ACTIVE' || key.status === 'ISSUED'
    )
    yield put(actions.auth.setApiKeys(reverse(activeKeys)))
  }
}

const deactivate = function* () {
  const keyId = yield select(selectors.auth.getApiKeyToDeactivate)

  const data: Response<void> = yield call(deactivateKey, keyId)

  if (data.ok) {
    yield put(actions.apiKey.getKeys())
    yield put(actions.app.hideModal('API_KEY_DELETE'))
  }
}

const activate = function* (action: ActivateApiKeyActionType) {
  const data: Response<void> = yield call(activateKey, action.payload.id)

  if (data.ok && data.response === null) {
    yield put(actions.app.showModal('API_KEY_ACTIVATE'))
  } else {
    yield put(actions.app.showModal('API_KEY_ACTIVATE_FAIL'))
  }
}

export default function* apiKeyForm() {
  yield takeEvery(actionTypes.apiKey.CREATE, create)
  yield takeEvery(actionTypes.apiKey.GET_API_KEYS, getKeysSaga)
  yield takeEvery(actionTypes.apiKey.DEACTIVATE, deactivate)
  yield takeEvery(actionTypes.apiKey.ACTIVATE, activate)
}
